import { ITranscriptContainer, IParagraph } from '../models/interfaces';
import { TextElement, PunctElement } from '../models/TranscriptElement';
import { makeObservable, observable, computed, action } from 'mobx';
import { BaseService } from './base/BaseService';
import {
  ITimelineService,
  TimelineEvent,
  ElementPosition,
  TimelineMetadata
} from './interfaces/ITimelineService';

/**
 * Service for managing timeline visualization of transcript elements
 */
export class TimelineService extends BaseService<TimelineEvent> implements ITimelineService {
  // Element positions on the timeline
  private _elementPositions: Map<string, ElementPosition> = new Map();
  
  // Total duration of the timeline
  private _totalDuration: number = 0;
  
  // Current active element ID
  private _activeElementId: string | null = null;
  
  // Is the timeline processed and ready
  private _isProcessed: boolean = false;
  
  /**
   * Creates a new TimelineService instance
   */
  constructor() {
    super(false); // Don't use BaseService's makeObservable
    
    // Use makeObservable with no specific configuration
    makeObservable(this);
  }
  
  /**
   * Processes transcript data to create a timeline visualization
   * 
   * @param container The transcript container to process
   * @param audioDuration Optional total duration of the audio file
   * @returns Timeline metadata
   */
  processTranscript(container: ITranscriptContainer, audioDuration?: number): TimelineMetadata {
    // Clear existing positions
    this._elementPositions.clear();
    this._isProcessed = false;
    
    // Count of elements with timing data
    let elementCount = 0;
    let elementsWithTiming = 0;
    
    // Process all segments
    for (const segment of container.segments) {
      // Process all paragraphs in the segment
      for (const paragraph of segment.paragraphs) {
        // Process all elements in the paragraph
        this.processElementsInParagraph(segment.id, paragraph);
        
        // Count elements
        elementCount += paragraph.elements.length;
        
        // Count elements with timing
        elementsWithTiming += paragraph.elements.filter(el => {
          if (el.type === 'text') {
            const textEl = el as TextElement;
            return typeof textEl.startTime === 'number' && typeof textEl.endTime === 'number';
          }
          return false;
        }).length;
      }
    }
    
    // Set total duration based on the last element's end time or provided audio duration
    this._totalDuration = this.calculateTotalDuration(audioDuration);
    
    // Mark as processed
    this._isProcessed = true;
    
    // Notify observers
    this.notify(TimelineEvent.ELEMENT_POSITIONS_CHANGED, this._elementPositions);
    this.notify(TimelineEvent.PROCESSING_COMPLETE, true);
    
    return {
      totalDuration: this._totalDuration,
      elementCount,
      hasTiming: elementsWithTiming > 0
    };
  }
  
  /**
   * Process elements in a paragraph for timeline positioning
   * 
   * @param segmentId ID of the parent segment
   * @param paragraph Paragraph to process
   */
  private processElementsInParagraph(segmentId: string, paragraph: IParagraph): void {
    const elements = paragraph.elements;
    
    for (let i = 0; i < elements.length; i++) {
      const element = elements[i];
      const nextElement = i < elements.length - 1 ? elements[i + 1] : null;
      
      // Skip elements without proper timing data
      if (element.type === 'text') {
        const textEl = element as TextElement;
        if (typeof textEl.startTime !== 'number' || typeof textEl.endTime !== 'number') {
          continue;
        }
        
        // Process text element
        this.processTextElement(segmentId, paragraph.id, textEl, nextElement);
      }
      else if (element.type === 'punct') {
        const punctEl = element as PunctElement;
        // Handle punctuation (usually should be merged with previous text element)
        this.processPunctElement(segmentId, paragraph.id, punctEl);
      }
    }
  }
  
  /**
   * Process a text element for timeline positioning
   * 
   * @param segmentId ID of the parent segment
   * @param paragraphId ID of the parent paragraph
   * @param element Text element to process
   * @param nextElement Next element in the paragraph (if any)
   */
  private processTextElement(
    segmentId: string, 
    paragraphId: string, 
    element: TextElement,
    nextElement: TextElement | PunctElement | null
  ): void {
    // Determine if we should include the next punctuation in this element's display text
    let displayText = element.value || '';
    let endTime = element.endTime!;
    
    // If next element is punctuation and has similar timing, merge it visually
    if (nextElement && nextElement.type === 'punct') {
      const punctEl = nextElement as PunctElement;
      const punctStartTime = (punctEl as any).startTime;
      const punctEndTime = (punctEl as any).endTime;
      
      if (typeof punctStartTime === 'number' && typeof punctEndTime === 'number') {
        const timeDiff = Math.abs(element.endTime! - punctStartTime);
        
        // If punctuation starts within 0.1s of text ending, include it visually
        if (timeDiff < 0.1) {
          displayText += punctEl.value || '';
          endTime = punctEndTime;
        }
      }
    }
    
    // Create unique position ID
    const positionId = `pos_${element.id}`;
    
    // Add element position to the map
    this._elementPositions.set(positionId, {
      id: positionId,
      elementId: element.id,
      startTime: element.startTime!,
      endTime,
      text: displayText,
      isPunctuation: false,
      paragraphId,
      segmentId
    });
  }
  
  /**
   * Process a punctuation element for timeline positioning
   * 
   * @param segmentId ID of the parent segment
   * @param paragraphId ID of the parent paragraph
   * @param element Punctuation element to process
   */
  private processPunctElement(
    segmentId: string, 
    paragraphId: string, 
    element: PunctElement
  ): void {
    // Only add standalone punctuation (those not merged with text elements)
    // This is typically for punctuation with significant timing differences
    const startTime = (element as any).startTime;
    const endTime = (element as any).endTime;
    
    if (typeof startTime === 'number' && typeof endTime === 'number') {
      // Create unique position ID
      const positionId = `pos_${element.id}`;
      
      this._elementPositions.set(positionId, {
        id: positionId,
        elementId: element.id,
        startTime,
        endTime,
        text: element.value || '',
        isPunctuation: true,
        paragraphId,
        segmentId
      });
    }
  }
  
  /**
   * Calculate the total duration based on element timing or provided audio duration
   * 
   * @param audioDuration Optional audio duration
   * @returns Total timeline duration
   */
  private calculateTotalDuration(audioDuration?: number): number {
    if (audioDuration && audioDuration > 0) {
      return audioDuration;
    }
    
    // Find the maximum end time among all elements
    let maxEndTime = 0;
    
    // Convert to array first to avoid iteration issues in older TS targets
    Array.from(this._elementPositions.values()).forEach(position => {
      if (position.endTime > maxEndTime) {
        maxEndTime = position.endTime;
      }
    });
    
    // Add a small buffer (5% or at least 1 second)
    return Math.max(maxEndTime + Math.max(1, maxEndTime * 0.05), 10);
  }
  
  /**
   * Get all element positions for timeline visualization
   * 
   * @returns Map of element positions by ID
   */
  getElementPositions(): Map<string, ElementPosition> {
    return new Map(this._elementPositions);
  }
  
  /**
   * Find the element at a specific time
   * 
   * @param time Time in seconds
   * @returns The element position at the given time, or null if none
   */
  getElementAtTime(time: number): ElementPosition | null {
    // Convert to array first to avoid iteration issues in older TS targets
    const positions = Array.from(this._elementPositions.values());
    
    for (let i = 0; i < positions.length; i++) {
      const position = positions[i];
      if (time >= position.startTime && time <= position.endTime) {
        if (this._activeElementId !== position.id) {
          this._activeElementId = position.id;
          this.notify(TimelineEvent.ACTIVE_ELEMENT_CHANGED, position);
        }
        return position;
      }
    }
    
    if (this._activeElementId !== null) {
      this._activeElementId = null;
      this.notify(TimelineEvent.ACTIVE_ELEMENT_CHANGED, null);
    }
    
    return null;
  }
  
  /**
   * Get the position of a specific element
   * 
   * @param elementId Element ID
   * @returns Element position or null if not found
   */
  getElementPosition(elementId: string): ElementPosition | null {
    // Find the position with matching elementId
    for (const position of this._elementPositions.values()) {
      if (position.elementId === elementId) {
        return position;
      }
    }
    return null;
  }
  
  /**
   * Clears the timeline
   */
  clear(): void {
    this._elementPositions.clear();
    this._totalDuration = 0;
    this._activeElementId = null;
    this._isProcessed = false;
    
    // Notify observers
    this.notify(TimelineEvent.ELEMENT_POSITIONS_CHANGED, this._elementPositions);
    this.notify(TimelineEvent.ACTIVE_ELEMENT_CHANGED, null);
  }
  
  /**
   * Check if the timeline has been processed
   */
  isProcessed(): boolean {
    return this._isProcessed;
  }
  
  
  /**
   * Set the active element ID
   * 
   * @param elementId The element ID to set as active, or null to clear
   */
  setActiveElement(elementId: string | null): void {
    this._activeElementId = elementId;
    
    // Find element position if element ID is provided
    let position: ElementPosition | null = null;
    if (elementId) {
      // Find position with matching element ID
      for (const pos of this._elementPositions.values()) {
        if (pos.elementId === elementId) {
          position = pos;
          break;
        }
      }
    }
    
    // Notify observers
    this.notify(TimelineEvent.ACTIVE_ELEMENT_CHANGED, position);
  }
  
  /**
   * Clean up resources
   */
  override cleanup(): void {
    super.cleanup();
    this._elementPositions.clear();
    this._totalDuration = 0;
    this._activeElementId = null;
    this._isProcessed = false;
  }
  
  // Public getters
  get totalDuration(): number { return this._totalDuration; }
  get activeElementId(): string | null { return this._activeElementId; }
}